import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Users, Leaf, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import OrganizationSchema from '../components/OrganizationSchema';
import FAQSchema from '../components/FAQSchema';
import ReviewsCarousel from '../components/ReviewsCarousel';
import ReviewForm from '../components/ReviewForm';
import { getReviews } from '../services/reviews-api';

const Home = () => {
  const { t, language } = useLanguage();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const data = await getReviews();
      setReviews(data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const handleReviewSubmitted = (newReview) => {
    setReviews([newReview, ...reviews]);
  };

  return (
    <div className="min-h-screen">
      <SEO
        titleEn="Sustainable Upcycling Fashion & Workshops Warsaw"
        titlePl="Zrównoważona Moda i Warsztaty Upcyklingu Warszawa"
        descriptionEn="Discover unique upcycled fashion and hands-on sewing workshops in Warsaw. Transform your wardrobe with Unya Mee's sustainable creative studio. Handmade pieces born from intuition. Warsztaty szycia dla początkujących i zaawansowanych."
        descriptionPl="Odkryj unikalną modę z upcyklingu i warsztaty szycia w Warszawie. Przekształć swoją garderobę w zrównoważonym studiu kreatywnym Unya Mee. Ręcznie robione ubrania z duszą. Sewing workshops for beginners and advanced."
        keywords="sustainable fashion Warsaw, upcycling workshops Warsaw, sewing classes Warsaw, eco fashion Poland, warsztaty szycia Warszawa, warsztaty upcyklingu Warszawa, zrównoważona moda Warszawa, kursy szycia Warszawa, beginner sewing Warsaw, advanced sewing workshops, handmade fashion Poland, sustainable clothing, creative workshops Warsaw, fashion upcycling Poland"
        image="https://www.unyamee.shop/images/workshops/main.jpg"
      />
      <OrganizationSchema />
      <FAQSchema />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <picture>
            <source srcSet="/images/workshops/main.webp" type="image/webp" />
            <img
              src="/images/workshops/main.jpg"
              alt="Unya Mee sustainable upcycling fashion studio Warsaw"
              width={1600}
              height={900}
              loading="eager"
              fetchpriority="high"
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center' }}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-br from-charcoal/70 via-charcoal/60 to-warm-sage/50" />
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto text-center space-y-8 py-24 relative z-10">
          <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl lg:text-8xl text-cream-white leading-tight animate-in fade-in slide-in-from-bottom-4 duration-700">
            {t('heroTitle')}
          </h1>
          <p className="text-cream-white text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-6 duration-700 delay-150">
            {t('heroSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300">
            <Link
              to="/workshops"
              className="group px-8 py-4 bg-warm-sage text-cream-white rounded uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:bg-rich-chocolate hover:shadow-lg flex items-center space-x-2"
            >
              <span>{t('heroCtaWorkshops')}</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/shop"
              className="group px-8 py-4 bg-cream-white border-2 border-charcoal text-charcoal rounded uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:bg-charcoal hover:text-cream-white flex items-center space-x-2"
            >
              <span>{t('heroCtaShop')}</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Upcycling Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-soft-gray relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center space-y-6 relative z-10">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-charcoal">{t('whyTitle')}</h2>
          <p className="text-warm-gray text-base sm:text-lg leading-relaxed">
            {t('whyText')}
          </p>
        </div>
      </section>

      {/* What Makes Us Unique */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-charcoal">{t('uniqueTitle')}</h2>
            <p className="text-warm-gray text-lg">{t('uniqueSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="group p-8 bg-cream-white rounded-lg border border-soft-gray hover:border-warm-sage transition-all duration-300 hover:shadow-md">
              <div className="w-12 h-12 rounded-full bg-warm-sage/10 flex items-center justify-center mb-6 group-hover:bg-warm-sage/20 transition-colors">
                <Sparkles className="text-warm-sage" size={24} />
              </div>
              <h3 className="font-serif text-2xl text-charcoal mb-4">{t('unique1Title')}</h3>
              <p className="text-warm-gray leading-relaxed">{t('unique1Text')}</p>
            </div>

            <div className="group p-8 bg-cream-white rounded-lg border border-soft-gray hover:border-warm-sage transition-all duration-300 hover:shadow-md">
              <div className="w-12 h-12 rounded-full bg-warm-sage/10 flex items-center justify-center mb-6 group-hover:bg-warm-sage/20 transition-colors">
                <Users className="text-warm-sage" size={24} />
              </div>
              <h3 className="font-serif text-2xl text-charcoal mb-4">{t('unique2Title')}</h3>
              <p className="text-warm-gray leading-relaxed">{t('unique2Text')}</p>
            </div>

            <div className="group p-8 bg-cream-white rounded-lg border border-soft-gray hover:border-warm-sage transition-all duration-300 hover:shadow-md">
              <div className="w-12 h-12 rounded-full bg-warm-sage/10 flex items-center justify-center mb-6 group-hover:bg-warm-sage/20 transition-colors">
                <Leaf className="text-warm-sage" size={24} />
              </div>
              <h3 className="font-serif text-2xl text-charcoal mb-4">{t('unique3Title')}</h3>
              <p className="text-warm-gray leading-relaxed">{t('unique3Text')}</p>
            </div>

            <div className="group p-8 bg-cream-white rounded-lg border border-soft-gray hover:border-warm-sage transition-all duration-300 hover:shadow-md">
              <div className="w-12 h-12 rounded-full bg-warm-sage/10 flex items-center justify-center mb-6 group-hover:bg-warm-sage/20 transition-colors">
                <Heart className="text-warm-sage" size={24} />
              </div>
              <h3 className="font-serif text-2xl text-charcoal mb-4">{t('unique4Title')}</h3>
              <p className="text-warm-gray leading-relaxed">{t('unique4Text')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Art of Making */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-soft-gray">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-charcoal mb-4">The Art of Making</h2>
            <p className="text-warm-gray text-lg">Every piece tells a story of transformation</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="relative h-96 rounded-lg overflow-hidden group shadow-xl">
              <picture>
                <source srcSet="/images/workshops/craft.webp" type="image/webp" />
                <img
                  src="/images/workshops/craft.jpg"
                  alt="Unya Mee workshop - hands creating together in Warsaw studio"
                  width={600}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700 ease-out"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
              <div className="absolute bottom-8 left-8 text-cream-white transform group-hover:translate-y-[-4px] transition-transform duration-500">
                <h3 className="font-serif text-3xl mb-2">Craft</h3>
                <p className="text-sm opacity-90">Hands that listen to fabric</p>
              </div>
            </div>

            <div className="relative h-96 rounded-lg overflow-hidden group shadow-xl">
              <picture>
                <source srcSet="/images/workshops/tranform.webp" type="image/webp" />
                <img
                  src="/images/workshops/tranform.jpg"
                  alt="Unya Mee textile transformation upcycling process"
                  width={600}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700 ease-out"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
              <div className="absolute bottom-8 left-8 text-cream-white transform group-hover:translate-y-[-4px] transition-transform duration-500">
                <h3 className="font-serif text-3xl mb-2">Transform</h3>
                <p className="text-sm opacity-90">Waste becomes wonder</p>
              </div>
            </div>

            <div className="relative h-96 rounded-lg overflow-hidden group shadow-xl">
              <picture>
                <source srcSet="/images/workshops/create.webp" type="image/webp" />
                <img
                  src="/images/workshops/create.jpg"
                  alt="Unya Mee unique handmade creation Warsaw"
                  width={600}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700 ease-out"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
              <div className="absolute bottom-8 left-8 text-cream-white transform group-hover:translate-y-[-4px] transition-transform duration-500">
                <h3 className="font-serif text-3xl mb-2">Create</h3>
                <p className="text-sm opacity-90">One-of-a-kind pieces</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-warm-sage/5 via-soft-taupe/10 to-cream-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal">
            Ready to Transform?
          </h2>
          <p className="text-warm-gray text-lg leading-relaxed max-w-2xl mx-auto">
            Join our intimate workshops or explore our collection of one-of-a-kind pieces. Every item has a story. Every workshop is a journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            <Link
              to="/workshops"
              className="group px-8 py-4 bg-warm-sage text-cream-white rounded uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:bg-rich-chocolate hover:shadow-lg flex items-center space-x-2"
            >
              <span>Explore Workshops</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/shop"
              className="group px-8 py-4 bg-transparent border-2 border-charcoal text-charcoal rounded uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:bg-charcoal hover:text-cream-white flex items-center space-x-2"
            >
              <span>View Collection</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials / Reviews Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-soft-taupe/20 to-warm-sage/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">
              {language === 'pl' ? 'Co Mówią Nasi Uczniowie' : 'What Our Students Say'}
            </h2>
            <p className="text-warm-gray text-lg">
              {language === 'pl' ? 'Prawdziwe opinie od prawdziwych ludzi ✨' : 'Real stories from real people ✨'}
            </p>
          </div>

          {/* Reviews Carousel */}
          <div className="mb-16">
            <ReviewsCarousel reviews={reviews} />
          </div>

          {/* Submit Review Form */}
          <div className="max-w-2xl mx-auto">
            <ReviewForm onReviewSubmitted={handleReviewSubmitted} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;